import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Upload,
  BookOpen,
  Building2,
  Receipt,
  ScrollText,
  Users,
  Shield,
  ChevronDown,
  LogOut,
  Bell,
  Activity as ActivityIcon,
  UserCog,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import ForcePasswordChange from "@/components/ForcePasswordChange";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

const NAV = [
  {
    section: "Workspace",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard },
      { to: "/activity", label: "Activity", icon: ActivityIcon },
    ],
  },
  {
    section: "Insurance",
    items: [
      { to: "/policies", label: "Policies", icon: FileText },
      { to: "/journal-entries", label: "Journal Entries", icon: Receipt },
      { to: "/upload", label: "Upload Data", icon: Upload },
    ],
  },
  {
    section: "Reference",
    items: [
      { to: "/reference/broker-accounts", label: "Broker Accounts", icon: Building2 },
      { to: "/reference/master-policy", label: "Master Policy", icon: BookOpen },
      { to: "/reference/siriuspoint", label: "SiriusPoint", icon: Shield },
      { to: "/reference/tax-rates", label: "Tax Rates", icon: ScrollText },
      { to: "/reference/commission-notes", label: "Commission Notes", icon: Users },
    ],
  },
  {
    section: "Administration",
    adminOnly: true,
    items: [
      { to: "/users", label: "Users & access", icon: UserCog },
    ],
  },
];

const PAGE_TITLES = {
  "/": ["Dashboard", "Overview"],
  "/activity": ["Activity log", "All workspace changes"],
  "/policies": ["Policies", "Inception-to-Date Production"],
  "/upload": ["Upload Data", "Append monthly Excel"],
  "/journal-entries": ["Journal Entries", "Segment Roll-ups"],
  "/reference/broker-accounts": ["Broker Accounts", "Reference"],
  "/reference/master-policy": ["Master Policy", "Reference"],
  "/reference/siriuspoint": ["SiriusPoint", "Reference"],
  "/reference/tax-rates": ["Tax Rates", "Reference"],
  "/reference/commission-notes": ["Commission Notes", "Reference"],
  "/users": ["Users & access", "Administration"],
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const [title, sub] = PAGE_TITLES[loc.pathname] || ["MGA Manager", ""];
  const initials = (user?.name || user?.email || "?")
    .split(/\s+|@/)[0]
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen flex bg-slate-50 text-slate-900">
      <ForcePasswordChange />
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className="w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col"
      >
        <div className="h-16 px-5 flex items-center border-b border-slate-200">
          <a href="/" className="flex items-center" data-testid="app-logo">
            <img
              src="/brand/players-health-logo.png"
              alt="Players Health"
              className="h-7 w-auto object-contain"
            />
          </a>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
          {NAV.filter((g) => !g.adminOnly || user?.role === "admin").map((group) => (
            <div key={group.section}>
              <div className="px-3 mb-2 text-[11px] font-medium uppercase tracking-wider text-slate-400">
                {group.section}
              </div>
              <ul className="space-y-0.5">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.to === "/"}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        [
                          "group flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                          isActive
                            ? "bg-blue-50 text-blue-700 font-medium"
                            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
                        ].join(" ")
                      }
                    >
                      <item.icon
                        className="w-[18px] h-[18px] shrink-0"
                        strokeWidth={1.75}
                      />
                      <span className="truncate">{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="px-3 py-3 border-t border-slate-200 text-[11px] text-slate-400">
          v1.0 • Inception-to-Date
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-8">
          <div>
            <div className="text-[11px] text-slate-400 uppercase tracking-wider">{sub}</div>
            <h1 className="text-[17px] font-semibold tracking-tight text-slate-900 leading-tight">
              {title}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="rounded-full" data-testid="topbar-bell">
              <Bell className="w-[18px] h-[18px] text-slate-500" strokeWidth={1.75} />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-slate-100 transition"
                >
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-blue-100 text-blue-700 text-[12px] font-medium">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left">
                    <div className="text-[13px] font-medium leading-none">
                      {user?.name || user?.email}
                    </div>
                    <div className="text-[11px] text-slate-500 leading-none mt-1">
                      {user?.role}
                    </div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={logout}
                  data-testid="user-menu-logout"
                  className="text-red-600 focus:text-red-700"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto px-8 py-8">{children}</main>
      </div>
    </div>
  );
}
